import { MEASURES, Entities, Periods, timeRangeToStepsParams, Channel } from 'cio/utils/metrics/common';
import { Metric, TOTAL_MEASURES } from 'cio/utils/metrics/models/metric';
import moment from 'moment-timezone';
import { Grouping } from 'cio/utils/metrics/resolution';
import { TimeRange } from 'cio/utils/time';
import DS from 'ember-data';

export function deserializeCampaignActionMetrics(
  data: { action_metrics: Record<string, any> },
  campaignId: number | string,
  period: Periods,
  grouping: Grouping,
  resolution: Periods,
  range: TimeRange | null,
  apiRange: TimeRange | null,
  meta: {
    version?: number;
    environmentId?: number | string;
    campaignId?: number | string;
    canConvert?: boolean;
  } | null,
  store: DS.Store,
) {
  if (data.action_metrics == null) {
    return null;
  }

  let _meta: { version?: number; environmentId?: number | string; campaignId?: number | string; canConvert?: boolean } =
    meta != null ? { ...meta } : {};
  _meta.campaignId = campaignId;

  return Object.keys(data.action_metrics).reduce<Record<string, Metric>>((acc, id) => {
    let metric = data.action_metrics[id];

    let actionMeta = { ..._meta };

    if (store != null) {
      let actionModel = store.peekRecord('action', id);
      if (actionModel != null) {
        actionMeta.canConvert = actionModel.canConvert;
      }
    }

    let deserialized = deserialize(
      { metric },
      Entities.ACTION,
      id,
      period,
      grouping,
      resolution,
      range,
      apiRange,
      metric.type,
      actionMeta,
    );

    if (deserialized != null) {
      acc[id] = deserialized as Metric;
    }

    return acc;
  }, {});
}

export function deserializeCampaignChannelMetrics(
  data: { channel_metrics: Record<string, any> },
  campaignId: number | string,
  period: Periods,
  grouping: Grouping,
  resolution: Periods,
  range: TimeRange | null,
  apiRange: TimeRange | null,
  meta?: { version?: number; environmentId?: number | string; campaignId?: number | string },
) {
  if (data.channel_metrics == null) {
    return null;
  }

  return Object.keys(data.channel_metrics).reduce<Partial<Record<Channel, Metric>>>((acc, channel) => {
    let metric = data.channel_metrics[channel];

    let deserialized = deserialize(
      { metric },
      Entities.CAMPAIGN,
      campaignId,
      period,
      grouping,
      resolution,
      range,
      apiRange,
      metric.type,
      meta,
    );

    if (deserialized != null) {
      acc[channel as Channel] = deserialized as Metric;
    }

    return acc;
  }, {});
}

export function deserialize(
  data: any,
  entityType: Entities,
  entityId: number | string,
  period: Periods,
  grouping: Grouping,
  resolution: Periods,
  range: TimeRange | null,
  apiRange: TimeRange | null,
  actionType: string | null,
  meta?: { environmentId?: number | string; campaignId?: number | string; canConvert?: boolean; version?: number },
): null | Metric[] | Metric | Record<string, Metric> {
  let useV2 = meta?.version === 2;

  if (!data.metrics && !data.metric) {
    return null;
  }

  if (data.metrics) {
    return data.metrics.map((metric: any) =>
      deserialize({ metric }, entityType, entityId, period, grouping, resolution, range, apiRange, metric.type, meta),
    );
  }

  let processedData: Record<string, any> = {};

  let _range = {
    start: range?.start,
    end: range?.end,
  };

  if (useV2) {
    let timestamps: number[] = [];

    if ((apiRange?.start == null || apiRange?.end == null) && resolution === Periods.DAYS) {
      // if a range isnt provided and the resolution is "days", the backend pulls the last 45 days
      let end = moment.tz('Etc/GMT+4').startOf('day');
      timestamps = [...new Array(45)].map((_, i) => end.unix() - i * 24 * 60 * 60).reverse();

      _range = {
        start: end.clone().subtract(29, 'days'),
        end,
      };

      MEASURES.forEach((measure) => {
        let measureData: any[] = timestamps.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: data.metric[measure]?.[i],
        }));

        processedData[measure] = measureData;
      });
    } else {
      if (resolution === Periods.DAYS) {
        let requestedRange = timeRangeToStepsParams(apiRange, 'Etc/GMT+4');
        if (requestedRange?.steps != null && requestedRange?.start != null) {
          let { start, steps } = requestedRange;
          timestamps = [start, ...[...new Array(steps)].map((_, i) => start + (i + 1) * 24 * 60 * 60)].reverse();
        }
      } else if (resolution === Periods.WEEKS) {
        let requestedRange = timeRangeToStepsParams(apiRange, 'Etc/GMT+4', 'week');
        if (requestedRange?.steps != null && requestedRange?.start != null) {
          let { start, steps } = requestedRange;
          timestamps = [start, ...[...new Array(steps)].map((_, i) => start + (i + 6) * 24 * 60 * 60)].reverse();
        }
      } else if (resolution === Periods.MONTHS) {
        let requestedRange = timeRangeToStepsParams(apiRange, 'Etc/GMT+4', 'month');
        if (requestedRange?.steps != null && requestedRange?.start != null) {
          let { start, steps } = requestedRange;
          timestamps = [
            start,
            ...[...new Array(steps)].map((_, i) =>
              _range
                .start!.clone()
                .add(i + 1, 'month')
                .unix(),
            ),
          ].reverse();
        }
      } else if (resolution === Periods.HOURS) {
        let requestedRange = timeRangeToStepsParams(apiRange, 'Etc/GMT+4', 'hour');
        if (requestedRange?.steps != null && requestedRange?.start != null) {
          let { start, steps } = requestedRange;
          timestamps = [start, ...[...new Array(steps)].map((_, i) => start + (i + 1) * 60 * 60)].reverse();
        }
      }
      MEASURES.forEach((measure) => {
        // We map the data from the end of the metrics received from the API,
        // rather than the beginning

        let metricMeasureData: number[] = [];

        // all_deliveries metrics data is stored in the "values" prop
        if ('values' in data.metric) {
          metricMeasureData = data.metric.values[measure] ?? [];
        } else {
          metricMeasureData = data.metric[measure] ?? [];
        }

        let dataLength = metricMeasureData.length;

        let measureData: { x: number; y: number }[] = timestamps.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: metricMeasureData[dataLength - 1 - i],
        }));

        processedData[measure] = measureData.reverse();
      });
    }
  } else {
    if (period === Periods.CUSTOM) {
      _range = {
        start: moment.tz(range?.start?.format('YYYY-MM-DD'), 'Etc/GMT+4'),
        end: moment.tz(range?.end?.format('YYYY-MM-DD'), 'Etc/GMT+4'),
      };

      let timestamps: number[] = [];

      if (resolution === Periods.DAYS) {
        let requestedRange = timeRangeToStepsParams(apiRange);
        if (requestedRange?.steps != null && requestedRange?.start != null) {
          let { start, steps } = requestedRange;
          timestamps = [start, ...[...new Array(steps)].map((_, i) => start + (i + 1) * 24 * 60 * 60)].reverse();
        }
      } else if (resolution === Periods.MONTHS) {
        let start = moment(_range.start).startOf('month');
        let end = moment(_range.end).startOf('month');
        timestamps = [...new Array(end.diff(start, 'months'))].map((_, i) => end.clone().subtract(i, 'months').unix());
      }

      MEASURES.forEach((measure) => {
        // We map the data from the end of the metrics received from the API,
        // rather than the beginning
        let metricMeasureData = data.metric[measure] ?? [];
        let dataLength = metricMeasureData.length;

        let measureData: any[] = timestamps.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: metricMeasureData[dataLength - 1 - i],
        }));

        processedData[measure] = measureData.reverse();
      });
    } else if (period === Periods.DAYS || period === Periods.ALL_TIME) {
      // The backend doesn't have an "all_time" period - what we actually receive
      // from the API in this case is the "days" period data.
      let end = moment.tz('Etc/GMT+4').startOf('day');
      let timestamps = [...new Array(45)].map((_, i) => end.unix() - i * 24 * 60 * 60).reverse();
      MEASURES.forEach((measure) => {
        let measureData: any[] = timestamps.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: data.metric[measure]?.[i],
        }));

        processedData[measure] = measureData;
      });

      if (!range) {
        _range = {
          start: end.clone().subtract(29, 'days'),
          end,
        };
      }
    } else if (period === Periods.WEEKS) {
      let startOfTodayInEastern = moment.tz('Etc/GMT+4').startOf('day');
      // Weekly calculations in the backend assume weeks start on Sundays. Moment
      // only supports isoWeek, which always starts on Mondays, or week, which is
      // locale-dependent. We need to make sure to calculate weeks based on
      // Sundays in this place, so we find it ourselves.
      let end = startOfTodayInEastern.clone().subtract(startOfTodayInEastern.day(), 'days');
      let timestamps = [...new Array(12)].map((_, i) => end.unix() - i * 24 * 60 * 60 * 7).reverse();
      MEASURES.forEach((measure) => {
        let measureData: any[] = timestamps.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: data.metric[measure]?.[i],
        }));

        processedData[measure] = measureData;
      });

      if (!range) {
        _range = {
          start: end.clone().subtract(11, 'weeks'),
          end,
        };
      }
    } else if (period === Periods.MONTHS) {
      let end = moment.tz('Etc/GMT+4').startOf('month');
      let months = [...new Array(13)].map((_, i) => end.clone().subtract(i, 'months').unix()).reverse();
      MEASURES.forEach((measure) => {
        let measureData: any[] = months.map((timestamp, i) => ({
          x: timestamp * 1000,
          y: data.metric[measure]?.[i],
        }));

        processedData[measure] = measureData;
      });

      if (!range) {
        _range = {
          start: end.clone().subtract(12, 'months'),
          end,
        };
      }
    }
  }

  MEASURES.forEach((measure) => {
    processedData[TOTAL_MEASURES[measure]] = data.metric[`total_${measure}`];
  });

  processedData.unsentDrafts = data.metric.unsent_drafts;

  if (actionType != null && actionType !== 'all' && !actionType.includes('_action')) {
    actionType = `${actionType}_action`;
  }

  processedData.subscription_topic_metrics = data.metric.subscription_topic_metrics;

  let metric = new Metric({
    entityId,
    entityType,
    period,
    grouping,
    resolution,
    range: _range,
    apiRequestRange: apiRange,
    actionType: actionType ?? 'all',
    data: processedData,
    canConvert: meta?.canConvert ?? false,
    meta,
  });

  return metric;
}
